import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { doc, getDoc, collection, query, getDocs, where, updateDoc } from 'firebase/firestore';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { Tab } from '@headlessui/react';
import { classNames } from '../utils/classNames';

const MemberSearchWidget = ({ members = [], archivedMembers = [], pendingRequests = [], invitations = [], organizationId, onRoleChange, onRequestAction, onResend, onArchiveMember, onRestoreMember, onMemberCountChange, loading, defaultTab }) => {
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedTab, setSelectedTab] = useState(defaultTab || 'members'); // 'members' or 'pending' or 'invitations' or 'archived'
  const [membersPerPage = 30, setMembersPerPage] = useState(30);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showLastAdminWarning, setShowLastAdminWarning] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [adminCount, setAdminCount] = useState(0);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const processedInvites = useMemo(() => {
    if (!invitations || invitations.length === 0) {
      return [];
    }
    const invitesByEmail = invitations.reduce((acc, invite) => {
      const existingInvite = acc[invite.email];
      if (!existingInvite || invite.createdAt.seconds > existingInvite.createdAt.seconds) {
        acc[invite.email] = invite;
      }
      return acc;
    }, {});
    return Object.values(invitesByEmail).sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
  }, [invitations]);

  // Load member detail (phone, disciple count, org stories) when an org admin clicks a member
  useEffect(() => {
    if (!selectedMember || !organizationId) {
      setMemberDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setMemberDetail(null);
    const load = async () => {
      try {
        const userId = selectedMember.id;
        const [userSnap, orgStoriesSnap] = await Promise.all([
          getDoc(doc(db, 'users', userId)),
          getDocs(query(
            collection(db, 'organizations', organizationId, 'stories'),
            where('authorId', '==', userId)
          )),
        ]);
        if (cancelled) return;
        const userData = userSnap.exists() ? userSnap.data() : {};
        const discipleCount = (userData.discipleIds && userData.discipleIds.length) || 0;
        const storyRefs = orgStoriesSnap.docs || [];
        const storyCount = storyRefs.filter(d => d.id !== '_collection_init').length;
        setMemberDetail({
          phone: userData.phone || '',
          discipleCount,
          storyCount,
        });
      } catch (err) {
        if (!cancelled) setMemberDetail({ phone: '', discipleCount: 0, storyCount: 0 });
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedMember, organizationId]);

  useEffect(() => {
    if (!members) {
      setFilteredMembers([]);
      return;
    };
    
    let filtered = [...members];
    
    if (selectedLetter === 'admins') {
      filtered = members.filter(member => member.role === 'Admin');
    } else if (selectedLetter !== 'all') {
      filtered = members.filter(member => {
        if (!member.name) return false;
        
        if (selectedLetter === '#') {
          return !/^[a-zA-Z]/.test(member.name.charAt(0).toUpperCase());
        }
        return member.name.charAt(0).toUpperCase() === selectedLetter;
      });
    }

    setFilteredMembers(filtered);
    setCurrentPage(1);
    onMemberCountChange(members.length);
  }, [selectedLetter, members, onMemberCountChange]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / membersPerPage);
  const startIndex = (currentPage - 1) * membersPerPage;
  const currentMembers = filteredMembers.slice(startIndex, startIndex + membersPerPage);

  // Update the search functionality
  const handleSearch = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredMembers(members);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = members.filter(member => 
      member.name?.toLowerCase().includes(searchLower) ||
      member.email?.toLowerCase().includes(searchLower) ||
      member.appID?.toLowerCase().includes(searchLower)
    );

    setFilteredMembers(filtered);
    setCurrentPage(1);
  };

  const handleRoleChange = async (userId, currentRole, newRole) => {
    try {
      await onRoleChange(userId, currentRole, newRole);
    } catch (error) {
      console.error('Error updating member role:', error);
    }
  };

  const handleRequestAction = async (userId, action) => {
    try {
      await onRequestAction(userId, action);
    } catch (error) {
      console.error('Error handling membership request:', error);
    }
  };

  const RoleDropdown = ({ member, isLastAdmin }) => {
    const options = member.role === 'Admin' 
      ? ['Admin', 'Member', 'Remove User'] 
      : ['Member', 'Admin', 'Remove User'];

    return (
      <div className="relative inline">
        <button
          onClick={() => setOpenDropdown(openDropdown === member.id ? null : member.id)}
          className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center"
        >
          <span className="mr-1">{member.role}</span>
          <ChevronDownIcon className="h-3 w-3" />
        </button>

        {openDropdown === member.id && (
          <div className="absolute z-10 mt-1 w-36 bg-white rounded-md shadow-lg border border-gray-200">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  if (member.role === 'Admin' && option !== 'Admin' && isLastAdmin) {
                    setShowLastAdminWarning(true);
                    setUserToRemove({ userId: member.id, currentRole: member.role });
                    setOpenDropdown(null);
                  } else {
                    handleRoleChange(
                      member.id, 
                      member.role, 
                      option === 'Remove User' ? 'Remove' : option
                    );
                  }
                }}
                className={`block w-full text-left px-4 py-2 text-sm ${
                  option === member.role 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Add a safe date formatting function
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    return 'Recently';
  };

  const defaultIndex = defaultTab === 'pending' ? 1 : defaultTab === 'invitations' ? 2 : defaultTab === 'archived' ? 3 : 0;
  return (
    <div className="h-[80vh] flex flex-col">
      <Tab.Group defaultIndex={defaultIndex} onChange={(index) => setSelectedTab(index === 0 ? 'members' : index === 1 ? 'pending' : index === 2 ? 'invitations' : 'archived')}>
        <Tab.List className="border-b border-gray-200 mb-4 flex">
          <Tab className={({ selected }) => classNames(
            'px-4 py-2 text-sm font-medium',
            selected ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
          )}>
            Members ({members.length})
          </Tab>
          <Tab className={({ selected }) => classNames(
            'px-4 py-2 text-sm font-medium',
            selected ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
          )}>
            Pending ({pendingRequests.length})
          </Tab>
          <Tab className={({ selected }) => classNames(
            'px-4 py-2 text-sm font-medium',
            selected ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
          )}>
            Invitations ({processedInvites.length})
          </Tab>
          <div className="flex-1" />
          <Tab className={({ selected }) => classNames(
            'px-4 py-2 text-sm font-medium',
            selected ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
          )}>
            Archived ({archivedMembers.length})
          </Tab>
        </Tab.List>

        <Tab.Panels className="flex-1 overflow-y-auto">
          {/* Members Panel */}
          <Tab.Panel className="h-full flex flex-col">
            {/* Search Bar */}
            <div className="flex items-center space-x-2 mb-4 sticky top-0 bg-white p-2">
              <input
                type="search"
                placeholder="Search members..."
                className="flex-1 px-3 py-2 border rounded-lg text-sm"
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>

            {/* Alpha Filter */}
            <div className="flex flex-wrap gap-1 mb-4 px-2 sticky top-14 bg-white pb-2 border-b">
              <button
                onClick={() => setSelectedLetter('all')}
                className={`px-2 py-1 rounded text-xs ${
                  selectedLetter === 'all'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedLetter('admins')}
                className={`px-2 py-1 rounded text-xs ${
                  selectedLetter === 'admins'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Admins
              </button>
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  onClick={() => setSelectedLetter(letter)}
                  className={`px-2 py-1 rounded text-xs ${
                    selectedLetter === letter
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>

            {/* Members Grid */}
            <div className="flex-1 overflow-y-auto min-h-0 px-2">
              {loading ? (
                <div className="text-center py-4 text-gray-500">Loading members...</div>
              ) : members.length === 0 ? (
                <div className="text-center py-4 text-gray-500">No members found.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {currentMembers.map((member) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => setSelectedMember(member)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white transition-all"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {member.photo ? (
                            <img 
                              src={member.photo} 
                              alt="" 
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <UserCircleIcon className="w-10 h-10 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {member.name}
                            </p>
                            {member.licenseStatus === 'allocated' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Licensed</span>
                            )}
                            {member.licenseStatus === 'provisional' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">Provisional</span>
                            )}
                            {member.licenseStatus === 'revoked' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Revoked</span>
                            )}
                            {(!member.licenseStatus || (!member.licenseAllocated && member.licenseStatus !== 'revoked')) && !member.isCreator && member.role !== 'Creator' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">No license</span>
                            )}
                            {(member.role === 'Creator' || member.isCreator) && (!member.licenseStatus || !member.licenseAllocated) && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">License included</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {member.role}
                          </p>
                          <p className="text-sm text-gray-500">
                            Joined: {formatDate(member.joinedAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination Footer */}
            <div className="sticky bottom-0 bg-white border-t mt-4 p-2 flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {filteredMembers.length > 0 
                  ? `Showing ${startIndex + 1}-${Math.min(startIndex + membersPerPage, filteredMembers.length)} of ${filteredMembers.length}`
                  : 'No members found'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
            </div>
          </Tab.Panel>

          {/* Pending Panel */}
          <Tab.Panel className="h-full flex flex-col p-2 space-y-2">
            {pendingRequests.length === 0 ? (
              <div className="text-center text-gray-500 py-4">No pending requests.</div>
            ) : (
              pendingRequests.map(request => (
                <div key={request.userId} className="p-3 rounded-lg border flex justify-between items-center">
                  <div>
                    <p className="font-medium">{request.name}</p>
                    <p className="text-sm text-gray-500">{request.email}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => onRequestAction(request.userId, 'approve')} className="btn btn-sm btn-success">Approve</button>
                    <button onClick={() => onRequestAction(request.userId, 'deny')} className="btn btn-sm btn-danger">Deny</button>
                  </div>
                </div>
              ))
            )}
          </Tab.Panel>

          {/* Invitations Panel */}
          <Tab.Panel className="h-full flex flex-col p-2 space-y-2">
            {processedInvites.length === 0 ? (
                <div className="text-center text-gray-500 py-4">No pending invitations.</div>
              ) : (
                processedInvites.map(invite => (
                  <div key={invite.id} className="p-3 rounded-lg border flex justify-between items-center">
                    <div>
                      <p className="font-medium">{invite.email}</p>
                      <p className="text-sm text-gray-500">
                        Invited by {invite.inviterName} about {formatDistanceToNow(invite.createdAt.toDate())} ago
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => onResend(invite)} className="btn btn-sm btn-outline">Resend</button>
                      <button onClick={() => onRequestAction(null, 'deny', invite.id)} className="btn btn-sm btn-ghost">Revoke</button>
                    </div>
                  </div>
                ))
              )}
          </Tab.Panel>

          {/* Archived Panel */}
          <Tab.Panel className="h-full flex flex-col p-2 space-y-2">
            <p className="text-sm text-gray-500 mb-4">The following former members do not have access to the community.</p>
            {archivedMembers.length === 0 ? (
              <div className="text-center text-gray-500 py-4">No archived members.</div>
            ) : (
              archivedMembers.map((member) => (
                <div key={member.id} className="p-3 rounded-lg border flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {member.photo ? (
                      <img src={member.photo} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <UserCircleIcon className="w-10 h-10 text-gray-400" />
                    )}
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-gray-500">{member.email}</p>
                      <p className="text-xs text-gray-400">
                        Archived {member.archivedAt?.toDate?.()?.toLocaleDateString() ?? '—'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onRestoreMember?.(member.id)}
                    className="px-4 py-2 bg-[#3274B8] text-white rounded-lg hover:bg-[#2861A1] text-sm font-medium"
                  >
                    Restore
                  </button>
                </div>
              ))
            )}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Member detail modal (org admin view) */}
      {selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedMember(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Member</h3>
              <button type="button" onClick={() => setSelectedMember(null)} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="flex items-center gap-4 mb-4">
                {selectedMember.photo ? (
                  <img src={selectedMember.photo} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <UserCircleIcon className="w-16 h-16 text-gray-400" />
                )}
                <div>
                  <p className="font-medium text-gray-900">{selectedMember.name}</p>
                  <p className="text-sm text-gray-500">{selectedMember.role}</p>
                </div>
              </div>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="text-gray-900">{selectedMember.email || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Phone</dt>
                  <dd className="text-gray-900">{detailLoading ? '…' : (memberDetail?.phone || '—')}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Number of disciples</dt>
                  <dd className="text-gray-900">{detailLoading ? '…' : (memberDetail ? memberDetail.discipleCount : '—')}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Stories shared with this org</dt>
                  <dd className="text-gray-900">{detailLoading ? '…' : (memberDetail ? memberDetail.storyCount : '—')}</dd>
                </div>
              </dl>
              {onArchiveMember && !selectedMember?.isCreator && selectedMember?.role !== 'Creator' && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Archive ${selectedMember.name}? They will be removed from this organization and their license will be freed.`)) {
                        onArchiveMember(selectedMember.id);
                        setSelectedMember(null);
                      }
                    }}
                    className="text-sm text-[#E9967A] hover:text-[#CD8162] hover:underline"
                  >
                    archive
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Last Admin Warning Modal */}
      {showLastAdminWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md">
            <h3 className="text-lg font-bold mb-4">Warning: Last Admin</h3>
            <p className="mb-4">
              You are about to remove the last admin of this organization. 
              This action will archive the organization and make it inaccessible.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowLastAdminWarning(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleRoleChange(userToRemove.userId, userToRemove.currentRole, 'Remove');
                  setShowLastAdminWarning(false);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Archive Organization
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberSearchWidget;
